import { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ReactReduxContext } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';

import { useIntl } from '../../util/reactIntl';

// Changes the user has not saved: edited since the form got its values and, after a submit, since
// that submit. A form that is being submitted has none (its own redirect never asks).
// The submit handlers do not hand an API failure back to the form, so final-form counts a rejected
// save as a success. With saveFailed (the page's error state) the sent values stay unsaved, also
// where the form took them as its new start values (the Location step); once the user edits a
// field after that submit, the form's own «changed» decides (backlog #46). «Edits» is final-form's
// modifiedSinceLastSubmit, not dirtySinceLastSubmit: new start values built as new objects make
// the latter true with nothing edited.
const hasUnsavedChanges = (formState, hasChanges, saveFailed) => {
  const {
    dirty,
    submitting,
    submitSucceeded,
    dirtySinceLastSubmit,
    modifiedSinceLastSubmit,
  } = formState;
  const changed = typeof hasChanges === 'boolean' ? hasChanges : dirty;
  if (submitting) {
    return false;
  }
  if (saveFailed && submitSucceeded) {
    return changed || !modifiedSinceLastSubmit;
  }
  return changed && (!submitSucceeded || dirtySinceLastSubmit);
};

/**
 * Unsaved-changes guard (A6-01, backlog #10). Rendered inside a final-form form. While the form has
 * unsaved changes, leaving the page asks first: in-app links and browser Back / Forward get the
 * browser's native confirm with UnsavedChangesGuard.message (Q1); reload, closing the tab and typed
 * URLs get the browser's own beforeunload dialog. Nothing asks during the form's own submit and its
 * redirect, or once the user is logging out / logged out.
 *
 * Every check is made at the moment of leaving, from the live form and auth state.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.hasChanges] the form's own «something to save» (default: final-form dirty)
 * @param {boolean} [props.saveFailed] the page's error state says the form's last save was rejected
 * @returns {null}
 */
const UnsavedChangesGuard = props => {
  const { hasChanges, saveFailed = false } = props;
  const intl = useIntl();
  const history = useHistory();
  const form = useForm();
  const formState = useFormState({
    subscription: {
      dirty: true,
      submitting: true,
      submitSucceeded: true,
      dirtySinceLastSubmit: true,
      modifiedSinceLastSubmit: true,
    },
  });
  const reduxContext = useContext(ReactReduxContext);

  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  const saveFailedRef = useRef(saveFailed);
  saveFailedRef.current = saveFailed;

  const when = hasUnsavedChanges(formState, hasChanges, saveFailed);

  // Read when leaving: the latest form state, and logout (in progress or done) never asks
  const shouldAsk = () => {
    const auth = reduxContext?.store?.getState()?.auth;
    const isLoggingOut = !!auth && (auth.logoutInProgress || !auth.isAuthenticated);
    return (
      !isLoggingOut &&
      hasUnsavedChanges(form.getState(), hasChangesRef.current, saveFailedRef.current)
    );
  };

  useEffect(() => {
    if (!when || !history?.block) {
      return undefined;
    }
    const message = intl.formatMessage({ id: 'UnsavedChangesGuard.message' });
    // A string asks with the router's getUserConfirmation (window.confirm); undefined lets it go
    return history.block(() => (shouldAsk() ? message : undefined));
  }, [when, history]);

  useEffect(() => {
    if (!when || typeof window === 'undefined') {
      return undefined;
    }
    const handleBeforeUnload = event => {
      if (!shouldAsk()) {
        return undefined;
      }
      event.preventDefault();
      // Browsers show their own text; returnValue is what makes them ask
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  return null;
};

export default UnsavedChangesGuard;
